import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChartBarIcon, 
  UserGroupIcon, 
  CubeIcon, 
  ChartPieIcon 
} from '@heroicons/react/24/outline';
import { dashboardTheme } from '../../styles/dashboardTheme';

export type SectionType = 'activity' | 'team' | 'inventory' | 'analytics';

interface DonutNavigationProps {
  activeSection: SectionType;
  onSectionChange: (section: SectionType) => void;
}

interface SectionConfig {
  id: SectionType;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  gradient: string;
  clipPath: string;
  labelPosition: { top?: string; bottom?: string; left?: string; right?: string };
}

const sections: SectionConfig[] = [
  {
    id: 'activity',
    label: 'Activity',
    icon: ChartBarIcon,
    color: dashboardTheme.sections.activity.primary,
    gradient: dashboardTheme.sections.activity.gradient,
    clipPath: 'polygon(50% 0%, 75% 25%, 50% 50%, 25% 25%)', // Top diamond quadrant
    labelPosition: { top: '20%', left: '50%' }, // Centered in top diamond
  },
  {
    id: 'team',
    label: 'Team',
    icon: UserGroupIcon,
    color: dashboardTheme.sections.team.primary,
    gradient: dashboardTheme.sections.team.gradient,
    clipPath: 'polygon(50% 50%, 75% 25%, 100% 50%, 75% 75%)', // Right diamond quadrant
    labelPosition: { top: '50%', right: '20%' }, // Centered in right diamond
  },
  {
    id: 'inventory',
    label: 'Inventory',
    icon: CubeIcon,
    color: dashboardTheme.sections.inventory.primary,
    gradient: dashboardTheme.sections.inventory.gradient,
    clipPath: 'polygon(50% 50%, 75% 75%, 50% 100%, 25% 75%)', // Bottom diamond quadrant
    labelPosition: { bottom: '20%', left: '50%' }, // Centered in bottom diamond
  },
  {
    id: 'analytics',
    label: 'Analytics',
    icon: ChartPieIcon,
    color: dashboardTheme.sections.analytics.primary,
    gradient: dashboardTheme.sections.analytics.gradient,
    clipPath: 'polygon(50% 50%, 25% 75%, 0% 50%, 25% 25%)', // Left diamond quadrant
    labelPosition: { top: '50%', left: '20%' }, // Centered in left diamond
  },
];

const DonutNavigation: React.FC<DonutNavigationProps> = ({ activeSection, onSectionChange }) => {
  const size = 500; // Size of the diamond - increased
  const padding = 60; // Extra padding to prevent cropping

  return (
    <div className="relative flex items-center justify-center" style={{ width: size + padding * 2, height: size + padding * 2, padding: padding }}>
      {/* Diamond container with 4 triangular sections */}
      <motion.div
        className="relative"
        style={{
          width: size,
          height: size,
        }}
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8, ease: 'easeOut' }}
      >
        {/* Quadrants */}
        {sections.map((section, index) => {
          const isActive = activeSection === section.id;
          const Icon = section.icon;

          return (
            <motion.div
              key={section.id}
              className="absolute inset-0 cursor-pointer transition-all duration-300"
              style={{
                clipPath: section.clipPath,
                background: isActive ? section.gradient : section.color,
                zIndex: isActive ? 10 : 1,
              }}
              initial={{ scale: 0.25, transformOrigin: 'center center' }}
              animate={{
                scale: isActive ? 1.15 : 1,
                transformOrigin: 'center center',
              }}
              whileHover={{
                scale: isActive ? 1.15 : 1.08,
                transformOrigin: 'center center',
              }}
              transition={{
                duration: 0.3,
                delay: index * 0.1,
                type: 'spring',
                stiffness: 200,
              }}
              onClick={() => onSectionChange(section.id)}
            >
              {/* Glow effect on active */}
              {isActive && (
                <motion.div
                  className="absolute inset-0"
                  style={{
                    clipPath: section.clipPath,
                    background: section.gradient,
                    filter: 'blur(20px)',
                    opacity: 0.6,
                  }}
                  animate={{
                    opacity: [0.4, 0.7, 0.4],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: 'easeInOut',
                  }}
                />
              )}

              {/* Section label and icon */}
              <div
                className="absolute flex flex-col items-center gap-2"
                style={{
                  ...section.labelPosition,
                  transform: 'translate(-50%, -50%)',
                }}
              >
                <motion.div
                  animate={{
                    scale: isActive ? [1, 1.1, 1] : 1,
                  }}
                  transition={{
                    duration: 1.5,
                    repeat: isActive ? Infinity : 0,
                  }}
                >
                  <Icon className="w-8 h-8 text-white drop-shadow-lg" />
                </motion.div>
                <span className="text-white font-bold text-sm drop-shadow-lg">
                  {section.label}
                </span>
              </div>

              {/* Active border - using box-shadow instead of border to avoid artifacts */}
              {isActive && (
                <motion.div
                  className="absolute inset-0"
                  style={{
                    clipPath: section.clipPath,
                    boxShadow: 'inset 0 0 0 4px white',
                  }}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                />
              )}
            </motion.div>
          );
        })}

        {/* Center circle with logo */}
        <div
          className="absolute bg-white rounded-full shadow-2xl flex items-center justify-center overflow-hidden"
          style={{
            width: 130,
            height: 130,
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        >
          {/* Pulsing background */}
          <motion.div
            className="absolute inset-0 bg-gradient-to-br from-blue-50 to-purple-50 rounded-full"
            animate={{
              scale: [1, 1.1, 1],
              opacity: [0.5, 0.8, 0.5],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />

          {/* Logo/Text */}
          <motion.div
            className="relative z-10 text-center"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.6, type: 'spring', stiffness: 200 }}
          >
            <div className="text-2xl font-extrabold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              ST
            </div>
            <div className="text-xs font-semibold text-gray-600">Dashboard</div>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};

export default DonutNavigation;

